import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import { useConfig } from '../config/ConfigContext';
import FloatingShareButton from '../components/FloatingShareButton';
import { metaTagsService } from '../services/metaTagsService';

/**
 * About Page Component
 * Enhanced version with original styling and configurable sections
 * @returns {JSX.Element}
 */
const AboutPage = () => {
  // Detect if the user is on a mobile device
  const isMobile = window.innerWidth <= 768;

  // Get config and theme data from context
  const { config, theme, loading, error } = useConfig();

  // Update meta tags when component mounts or config changes
  useEffect(() => {
    if (config && !loading) {
      const logoUrl = config.logo_url || `${window.location.origin}/images/penta-lectures-logo.png`;
      const description = config.about_us?.content ? 
        config.about_us.content.substring(0, 160) + '...' : 
        `Learn more about ${config.name || 'our educational platform'} and our mission`;
      
      metaTagsService.updateMetaTags({
        title: `About - ${config.name || 'Penta University'}`,
        description: description,
        image: logoUrl,
        url: `${window.location.origin}/about`,
        type: 'website'
      });
    }

    // Cleanup function to reset meta tags when component unmounts
    return () => {
      if (config) {
        metaTagsService.resetToDefaults();
      }
    };
  }, [config, loading]);

  // Handle loading and error states
  if (loading) {
    const Layout = isMobile ? MobileLayout : DesktopLayout;
    return <Layout><div style={{ padding: '20px', textAlign: 'center' }}>Loading About Us content...</div></Layout>;
  }

  if (error || !config || config.about_us_enabled === false) {
    const Layout = isMobile ? MobileLayout : DesktopLayout;
    const errorMessage = error ? `Error: ${error.message}` : 'About Us content could not be loaded or is disabled.';
    return <Layout><div style={{ padding: '20px', textAlign: 'center' }}>{errorMessage} Please try again later.</div></Layout>;
  }

  // Extract content from config
  const aboutContent = config.about_us_content || {};
  
  // Support both flat structure and nested structure
  const pageTitle = aboutContent.title || 'Our Vision & Mission';
  const pageTagline = aboutContent.tagline || aboutContent.description || '';
  const introText = aboutContent.intro || null;
  
  // Support both old format (direct properties) and new format (sections array)
  const sections = [];
  
  // Check for old format first
  if (aboutContent.vision && typeof aboutContent.vision === 'string') {
    sections.push({
      id: 'vision',
      title: 'Vision Statement',
      content: aboutContent.vision,
      enabled: true
    });
  }
  
  if (aboutContent.mission && typeof aboutContent.mission === 'string') {
    sections.push({
      id: 'mission',
      title: 'Mission Statement',
      content: aboutContent.mission,
      enabled: true
    });
  }
  
  if (aboutContent.approach && typeof aboutContent.approach === 'string') {
    sections.push({
      id: 'approach',
      title: 'Our Approach',
      content: aboutContent.approach,
      enabled: true
    });
  }
  
  // Override with new format if available
  if (aboutContent.sections && Array.isArray(aboutContent.sections)) {
    sections.length = 0; // Clear old format sections
    aboutContent.sections.forEach(section => {
      if (section.enabled !== false && section.content) {
        sections.push(section);
      }
    });
  }
  
  // Check for nested vision/mission/approach objects (another format variant)
  if (aboutContent.vision && typeof aboutContent.vision === 'object' && aboutContent.vision.text) {
    const existingVision = sections.find(s => s.id === 'vision');
    if (!existingVision) {
      sections.push({
        id: 'vision',
        title: aboutContent.vision.title || 'Vision Statement',
        content: aboutContent.vision.text,
        enabled: true
      });
    }
  }
  
  if (aboutContent.mission && typeof aboutContent.mission === 'object' && aboutContent.mission.text) {
    const existingMission = sections.find(s => s.id === 'mission');
    if (!existingMission) {
      sections.push({
        id: 'mission',
        title: aboutContent.mission.title || 'Mission Statement',
        content: aboutContent.mission.text,
        enabled: true
      });
    }
  }
  
  if (aboutContent.approach && typeof aboutContent.approach === 'object' && aboutContent.approach.text) {
    const existingApproach = sections.find(s => s.id === 'approach');
    if (!existingApproach) {
      sections.push({
        id: 'approach',
        title: aboutContent.approach.title || 'Our Approach',
        content: aboutContent.approach.text,
        enabled: true
      });
    }
  }

  // Convert basic markdown syntax to React elements
  const renderFormattedText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, lineIdx) => {
      // Process inline formatting: **bold** and *italic*
      const parts = [];
      let remaining = line;
      let key = 0;
      while (remaining.length > 0) {
        // Bold: **text**
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        // Italic: *text* (but not inside **)
        const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);

        const nextMatch = [boldMatch, italicMatch]
          .filter(Boolean)
          .sort((a, b) => a.index - b.index)[0];

        if (!nextMatch) {
          parts.push(<React.Fragment key={key++}>{remaining}</React.Fragment>);
          break;
        }

        if (nextMatch.index > 0) {
          parts.push(<React.Fragment key={key++}>{remaining.slice(0, nextMatch.index)}</React.Fragment>);
        }

        if (nextMatch === boldMatch) {
          parts.push(<strong key={key++}>{nextMatch[1]}</strong>);
        } else {
          parts.push(<em key={key++}>{nextMatch[1]}</em>);
        }

        remaining = remaining.slice(nextMatch.index + nextMatch[0].length);
      }

      return (
        <React.Fragment key={lineIdx}>
          {lineIdx > 0 && <br />}
          {parts}
        </React.Fragment>
      );
    });
  };

  // Enhanced Section Component with decorative divider
  const Section = ({ title, content, showDivider = true, isLast = false }) => {
    return (
      <>
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{
            color: theme?.colors?.primary || '#1e4273',
            fontSize: isMobile ? '1.5rem' : '1.75rem',
            marginBottom: '16px',
            fontWeight: '600'
          }}>
            {title}
          </h2>
          <div style={{
            fontSize: isMobile ? '1rem' : '1.1rem',
            lineHeight: 1.6,
            color: theme?.colors?.text || '#333'
          }}>
            {renderFormattedText(content)}
          </div>
        </div>
        {showDivider && !isLast && (
          <div style={{
            height: '2px',
            background: `linear-gradient(90deg, ${theme?.colors?.primary || '#1e4273'}88 0%, ${theme?.colors?.secondary || '#f5f7fa'}88 50%, ${theme?.colors?.primary || '#1e4273'}88 100%)`,
            margin: '32px 0',
            borderRadius: '2px'
          }} />
        )}
      </>
    );
  };

  // Content sections for the About page
  const pageContent = (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '32px',
    }}>
      {/* Hero Banner with gradient */}
      <div style={{
        background: `linear-gradient(135deg, ${theme?.colors?.primary || '#1e4273'} 0%, ${theme?.colors?.secondary || '#f5f7fa'} 100%)`,
        borderRadius: '8px',
        padding: isMobile ? '32px 24px' : '64px 48px',
        marginBottom: '32px',
        textAlign: 'center',
        color: 'white',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ 
          fontSize: isMobile ? '2rem' : '2.75rem', 
          marginBottom: '16px',
          fontWeight: '700',
          letterSpacing: '-0.02em'
        }}>
          {pageTitle}
        </h1>
        {pageTagline && (
          <p style={{ 
            fontSize: isMobile ? '1rem' : '1.25rem', 
            opacity: 0.9, 
            maxWidth: '800px', 
            margin: '0 auto',
            lineHeight: 1.5
          }}>
            {pageTagline}
          </p>
        )}
        {introText && (
          <p style={{ 
            fontSize: isMobile ? '0.95rem' : '1.1rem',
            lineHeight: 1.6,
            color: 'white',
            opacity: 0.85,
            marginTop: '20px',
            maxWidth: '700px', 
            margin: '20px auto 0 auto'
          }}>
            {introText}
          </p>
        )}
      </div>

      {/* Render sections */}
      {sections.map((section, index) => (
        <Section 
          key={section.id || index}
          title={section.title}
          content={section.content}
          showDivider={section.show_divider !== false}
          isLast={index === sections.length - 1}
        />
      ))}

      {/* Developer Callout Section if present */}
      {aboutContent.developer_callout && (
        <>
          <div style={{ 
            height: '2px', 
            background: `linear-gradient(90deg, ${theme?.colors?.primary || '#1e4273'}88 0%, ${theme?.colors?.secondary || '#f5f7fa'}88 50%, ${theme?.colors?.primary || '#1e4273'}88 100%)`,
            margin: '32px 0',
            borderRadius: '2px'
          }} />
          <Section 
            title="A Note from the Developers"
            content={aboutContent.developer_callout}
            showDivider={false}
            isLast={true}
          />
        </>
      )}

      {/* Call to Action */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '48px',
        marginBottom: '24px'
      }}>
        {config.support_enabled === true ? (
          <Link to="/support" style={{
            display: 'inline-block',
            backgroundColor: theme?.colors?.primary || '#1e4273',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '4px',
            textDecoration: 'none',
            fontWeight: 'bold',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease',
            marginRight: '16px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme?.colors?.primaryDark || '#163152';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = theme?.colors?.primary || '#1e4273';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
          }}>
            Support Our Mission
          </Link>
        ) : null}
        <Link to="/" style={{
          display: 'inline-block',
          backgroundColor: 'transparent',
          color: theme?.colors?.primary || '#1e4273',
          padding: '12px 24px',
          borderRadius: '4px',
          border: `2px solid ${theme?.colors?.primary || '#1e4273'}`,
          textDecoration: 'none',
          fontWeight: 'bold',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme?.colors?.primary || '#1e4273';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = theme?.colors?.primary || '#1e4273';
        }}>
          Back to Home
        </Link>
      </div>
    </div>
  );

  // Render with the appropriate layout based on screen size
  return isMobile ? (
    <MobileLayout>
      {pageContent}
      <FloatingShareButton 
        pageTitle={`About - ${config?.name || ''}`}
        pageDescription={pageTagline || "Learn about our vision and mission"}
      />
    </MobileLayout>
  ) : (
    <DesktopLayout>
      {pageContent}
      <FloatingShareButton 
        pageTitle={`About - ${config?.name || ''}`}
        pageDescription={pageTagline || "Learn about our vision and mission"}
      />
    </DesktopLayout>
  );
};

export default AboutPage;